import API from "../../services/api";

const STATUS_TO_CODE = {
  present: "P",
  absent: "A",
  half_day: "HD",
  leave: "L",
  paid_leave: "PL",
  unpaid_leave: "UL",
  holiday: "H",
  wo: "WO",
  week_off: "WO",
  wfh: "WFH",
  on_duty: "OD",
  P: "P",
  A: "A",
  HD: "HD",
  L: "L",
  PL: "PL",
  UL: "UL",
  WO: "WO",
  H: "H",
  WFH: "WFH",
  OD: "OD",
};

export function normalizeAttendanceStatus(status) {
  if (!status) return "";
  const s = String(status).trim();
  return STATUS_TO_CODE[s] || STATUS_TO_CODE[s.toLowerCase()] || s;
}

const CODE_TO_API = {
  P: "present",
  A: "absent",
  HD: "half_day",
  L: "paid_leave",
  PL: "paid_leave",
  UL: "unpaid_leave",
  WO: "wo",
  H: "holiday",
  WFH: "wfh",
  OD: "on_duty",
};

/** Convert UI code to API / DB canonical status before save. */
export function toApiAttendanceStatus(status) {
  const code = normalizeAttendanceStatus(status);
  return CODE_TO_API[code] || code.toLowerCase();
}

function formatBulkErrors(errors) {
  if (!Array.isArray(errors) || !errors.length) return "";
  const parts = errors.slice(0, 3).map((e) => e.detail || e.message || JSON.stringify(e));
  const more = errors.length > 3 ? ` (+${errors.length - 3} more)` : "";
  return parts.join("; ") + more;
}

export async function approveSpecialWorkPremium(attendance_id, { approved = true, extra_payable_units = 1, notes = null } = {}) {
  try {
    const res = await API.post(`/attendance/${attendance_id}/special-work-premium`, {
      approved,
      extra_payable_units,
      notes,
    });
    return res.data;
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to update special work premium";
    throw new Error(msg);
  }
}

// 🔹 Fetch Attendance (Protected)
export async function fetchAttendance(params = {}) {
  try {
    const res = await API.get("/attendance/", { params });
    return res.data;
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to fetch attendance";
    throw new Error(msg);
  }
}

/** Employee self-service — own attendance only (GET /attendance/self). */
export async function fetchMyAttendance({ from_date, to_date } = {}) {
  try {
    const res = await API.get("/attendance/self", {
      params: { from_date, to_date },
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to fetch your attendance";
    throw new Error(msg);
  }
}

// 🔹 Create Attendance (Protected - Admin Only)
export async function createAttendance(data) {
  const payload = { ...data };
  if (payload.status) payload.status = toApiAttendanceStatus(payload.status);
  try {
    const res = await API.post("/attendance/", payload);
    return res.data;
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to create attendance";
    throw new Error(msg);
  }
}

// 🔹 Update Attendance (Protected - Admin Only)
export async function updateAttendance(attendance_id, data) {
  const payload = { ...data };
  if (payload.status) payload.status = toApiAttendanceStatus(payload.status);
  if (payload.work_hours !== undefined) payload.work_hours = Number(payload.work_hours || 0);
  try {
    const res = await API.put(`/attendance/${attendance_id}`, payload);
    return res.data;
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to update attendance";
    throw new Error(msg);
  }
}

export async function bulkUploadAttendance({ organisation_id, records, upsert = true }) {
  const payload = {
    organisation_id,
    upsert,
    records: (records || []).map((r) => ({
      ...r,
      status: toApiAttendanceStatus(r.status),
      work_hours: Number(r.work_hours || 0),
    })),
  };
  try {
    const res = await API.post("/attendance/bulk", payload);
    const data = res.data || {};
    const saved = (data.created || 0) + (data.updated || 0);
    const errList = data.errors || [];
    if (payload.records.length > 0 && saved === 0) {
      throw new Error(formatBulkErrors(errList) || "No attendance rows were saved");
    }
    if (errList.length > 0) {
      const partial = `${saved} saved, ${errList.length} failed: ${formatBulkErrors(errList)}`;
      const err = new Error(partial);
      err.partial = true;
      err.result = data;
      throw err;
    }
    return data;
  } catch (e) {
    if (e.partial) throw e;
    const detail = e?.response?.data?.detail;
    let msg = e?.message || "Failed to bulk upload attendance";
    if (typeof detail === "string") {
      msg = detail;
    } else if (Array.isArray(detail) && detail.length) {
      msg = detail
        .map((d) => {
          const path = Array.isArray(d?.loc) ? d.loc.join(".") : "";
          return `${path ? `${path}: ` : ""}${d?.msg || "Invalid value"}`;
        })
        .join(" | ");
    } else if (detail && typeof detail === "object") {
      msg = JSON.stringify(detail);
    }
    throw new Error(msg);
  }
}

export async function applyAttendanceCalendarJob({ organisation_id, from_date, to_date, employee_ids }) {
  const payload = { organisation_id, from_date, to_date, employee_ids: employee_ids || null };
  try {
    const res = await API.post("/attendance/jobs/apply-calendar", payload);
    return res.data;
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to apply calendar marks";
    throw new Error(msg);
  }
}

export async function upsertAttendanceRecord({ organisation_id, employee_id, work_date, status, work_hours = 0, remarks = null, comp_off_decision = null }) {
  return bulkUploadAttendance({
    organisation_id,
    upsert: true,
    records: [
      {
        employee_id,
        work_date,
        status,
        work_hours,
        remarks,
        ...(comp_off_decision ? { comp_off_decision } : {}),
      },
    ],
  });
}

export async function fetchLeaves({ employee_id } = {}) {
  try {
    const res = await API.get("/attendance/leave/", { params: { employee_id: employee_id || undefined } });
    return res.data;
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to fetch leaves";
    throw new Error(msg);
  }
}

export async function createLeave(data) {
  try {
    const res = await API.post("/attendance/leave/", data);
    return res.data;
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to create leave";
    throw new Error(msg);
  }
}

export async function approveLeave({ leave_id, decision = "approved", notes = null }) {
  try {
    const res = await API.post(`/attendance/leave/${leave_id}/approve`, { decision, notes });
    return res.data;
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to update leave";
    throw new Error(msg);
  }
}

export async function fetchPayPeriodAttendanceSummary(pay_period_id) {
  try {
    const res = await API.get(`/payrolls/pay-periods/${pay_period_id}/attendance-summary`);
    return res.data;
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to fetch pay period attendance summary";
    throw new Error(msg);
  }
}

export async function fetchOtAllocationStatus() {
  try {
    const res = await API.get("/attendance/ot-allocation/status");
    return res.data;
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to load OT allocation status";
    throw new Error(typeof msg === "string" ? msg : "Failed to load OT allocation status");
  }
}

export async function fetchOtAllocationMonth({ employee_id, year, month }) {
  try {
    const res = await API.get("/attendance/ot-allocation/month", {
      params: { employee_id, year, month },
    });
    return res.data;
  } catch (e) {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (status === 404) {
      throw new Error(
        "OT allocation API not found. Restart the backend (uvicorn on port 9000) and try again."
      );
    }
    throw new Error(
      (typeof detail === "string" ? detail : null) || e?.message || "Could not load OT allocation"
    );
  }
}

export async function fetchOtAllocationRegister({
  year,
  month,
  employee_id,
  status_filter,
  source_filter,
  search,
  page = 1,
  page_size = 10,
} = {}) {
  try {
    const res = await API.get("/attendance/ot-allocation/register", {
      params: {
        year,
        month,
        employee_id: employee_id || undefined,
        status_filter: status_filter || undefined,
        source_filter: source_filter || undefined,
        search: search || undefined,
        page,
        page_size,
      },
    });
    return res.data;
  } catch (e) {
    throw new Error(
      (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : null) ||
        e?.message ||
        "Could not load OT register"
    );
  }
}

export async function setOtAllocationStatus({ attendance_id, status, remarks }) {
  try {
    const res = await API.post("/attendance/ot-allocation/set-status", {
      attendance_id,
      status,
      remarks,
    });
    return res.data;
  } catch (e) {
    throw new Error(e?.response?.data?.detail || e?.message || "Failed to update OT status");
  }
}

export async function saveOtAllocation({ employee_id, work_date, hours, remarks }) {
  try {
    const res = await API.put("/attendance/ot-allocation", {
      employee_id,
      work_date,
      hours,
      remarks,
    });
    return res.data;
  } catch (e) {
    throw new Error(e?.response?.data?.detail || e?.message || "Failed to save OT allocation");
  }
}

export async function fetchOtPayPreview({ employee_id, work_date, hours, draft_rate_pct, draft_fixed_amount }) {
  try {
    const res = await API.get("/attendance/ot-allocation/pay-preview", {
      params: {
        employee_id,
        work_date,
        hours: hours || 0,
        draft_rate_pct: draft_rate_pct ?? undefined,
        draft_fixed_amount: draft_fixed_amount ?? undefined,
      },
    });
    return res.data;
  } catch (e) {
    throw new Error(
      (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : null) ||
        e?.message ||
        "Could not calculate OT pay"
    );
  }
}

export async function saveEmployeeOtRate({ employee_id, effective_from, rate_pct, fixed_amount, full_day_mode, remarks }) {
  try {
    const res = await API.put("/attendance/ot-allocation/employee-rate", {
      employee_id,
      effective_from,
      rate_pct,
      fixed_amount,
      full_day_mode,
      remarks,
    });
    return res.data;
  } catch (e) {
    throw new Error(
      (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : null) ||
        e?.message ||
        "Could not save this employee's OT rate"
    );
  }
}

export async function acceptDetectedOt(attendance_id) {
  try {
    const res = await API.post("/attendance/ot-allocation/accept-detected", { attendance_id });
    return res.data;
  } catch (e) {
    throw new Error(e?.response?.data?.detail || e?.message || "Failed to confirm OT");
  }
}

export async function enrollOtOccurrenceSlots({ employee_id, year, month, slot_count }) {
  try {
    const res = await API.post("/attendance/ot-allocation/occurrence-enroll", {
      employee_id,
      year,
      month,
      slot_count,
    });
    return res.data;
  } catch (e) {
    throw new Error(
      (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : null) ||
        e?.message ||
        "Failed to enroll OT occurrence slots"
    );
  }
}

export async function fetchMyOrganisationSummary() {
  try {
    const { loadOrganisationMe } = await import("../../services/appQueries");
    return await loadOrganisationMe();
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to fetch organisation";
    throw new Error(msg);
  }
}

export async function fetchMonthlyAttendanceCalendar({ year, month, location_id, department_id, employee_id } = {}) {
  try {
    const res = await API.get("/attendance/calendar/month", {
      params: { year, month, location_id, department_id, employee_id },
    });
    return res.data;
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || "Failed to load monthly calendar";
    throw new Error(msg);
  }
}

export async function bulkUpdateAttendanceStatus(payload) {
  const body = { ...payload };
  if (body.status) body.status = toApiAttendanceStatus(body.status);
  try {
    const res = await API.post("/attendance/bulk-status", body);
    const data = res.data || {};
    const saved = (data.created || 0) + (data.updated || 0);
    const errList = data.errors || [];
    if (saved === 0) {
      throw new Error(formatBulkErrors(errList) || "No attendance rows were updated");
    }
    if (errList.length > 0) {
      const err = new Error(
        `${saved} updated, ${errList.length} failed: ${formatBulkErrors(errList)}`
      );
      err.partial = true;
      err.result = data;
      throw err;
    }
    return data;
  } catch (e) {
    if (e.partial) throw e;
    const detail = e?.response?.data?.detail;
    let msg = e?.message || "Bulk status update failed";
    if (typeof detail === "string") {
      msg = detail;
    } else if (Array.isArray(detail) && detail.length) {
      msg = detail
        .map((d) => {
          const path = Array.isArray(d?.loc) ? d.loc.join(".") : "";
          return `${path ? `${path}: ` : ""}${d?.msg || "Invalid value"}`;
        })
        .join(" | ");
    } else if (detail && typeof detail === "object") {
      msg = detail.message || JSON.stringify(detail);
    }
    throw new Error(msg);
  }
}